import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useStore } from "react-redux";
import { Box } from "@mui/material";
import { ChatSurface } from "../ai-panel/ChatSurface";
import { useAiConnection } from "../../connection/AiConnectionProvider";
import { useNewChat, useResumeSession } from "../../hook/ai-panel";
import { useDrawerWidth } from "../../hook/layout";
import { useSideNavOpen } from "../../hook/sideNav";
import { RootState } from "../../reducer";

// The full-page chat fills the content frame — below the AppBar, right of the
// (persistent) side nav — and lets ChatSurface manage its own internal scroll.
// It is positioned to the frame directly rather than flowing inside
// PageContentBox's document padding (which would inset it and hide the header).
const APP_BAR_HEIGHT = 64; // MUI default Toolbar height (desktop)

export const ChatPage: React.FC = () => {
  const { activate } = useAiConnection();
  const newChat = useNewChat();
  const resume = useResumeSession();
  const navigate = useNavigate();
  const store = useStore<RootState>();
  const { chatId } = useParams();
  const drawerWidth = useDrawerWidth();
  const { sideNavOpen } = useSideNavOpen();
  const activeSessionId = useSelector((state: RootState) => state.ui.activeSessionId);

  // Rule A — the URL decides the chat. /newchat starts a fresh chat (the server session is reset and unbound, the transcript is cleared,
  // no session exists until the first message); /chat/:chatId opens that chat unless it is already the active one.
  useEffect(() => {
    activate();
    const active = store.getState().ui.activeSessionId;
    if (chatId) {
      if (chatId !== active) resume(chatId);
    } else {
      newChat();
    }
    // eslint-disable-next-line
  }, [chatId]);

  // Rule B — a new chat's first message mints a Bound Session; the address bar follows it, replacing /newchat so Back does not return
  // to an empty chat.
  // The live store value is read inside the effect: on entering /newchat the render-time activeSessionId is still the chat just left,
  // and Rule A (which runs first in the same commit) has already unbound it; acting on the stale value would send the URL back to it.
  useEffect(() => {
    const active = store.getState().ui.activeSessionId;
    if (!chatId && active) navigate(`/chat/${active}`, { replace: true });
  }, [activeSessionId, chatId, navigate, store]);

  return (
    <Box
      sx={{
        position: "fixed",
        top: `${APP_BAR_HEIGHT}px`,
        bottom: 0,
        right: 0,
        left: sideNavOpen ? drawerWidth : 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        transition: "left 0.2s ease",
      }}
    >
      <ChatSurface />
    </Box>
  );
};
